import React, { useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { enviarNotificacionAyuda } from "../api/usuarios";
import { showAlertExito, showAlertError } from "../utils/alertHelper";
import {
  FiBox,
  FiSettings,
  FiShoppingCart,
  FiLogOut,
  FiMenu,
  FiCopy,
  FiArchive,
  FiBookOpen,
  FiTruck,
  FiFolderPlus,
  FiMapPin,
  FiUserCheck,
  FiHome,
  FiUsers,
  FiUserX,
  FiUserPlus,
  FiShieldOff,
  FiShare2,
  FiDollarSign,
} from "react-icons/fi";

const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inventarioOpen, setInventarioOpen] = useState(false);
  const [ingresoOpen, setIngresoOpen] = useState(false);
  const [usuarioOpen, setUsuarioOpen] = useState(false);

  // Estados para ayuda
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpMessage, setHelpMessage] = useState("");
  const [helpLoading, setHelpLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();


  const cerrarSesion = () => {
    sessionStorage.removeItem("token");
    navigate("/");
  };

  const isRootDashboard = location.pathname === "/dashboard";

  const handleSendHelp = async (e) => {
    e.preventDefault();
    if (!helpMessage.trim()) return;
    setHelpLoading(true);
    try {
      const res = await enviarNotificacionAyuda({ mensaje: helpMessage });
      if (res && res.status === 1) {
        showAlertExito("Confirmación de Recibido", "Su mensaje ha sido recibido y se estará gestionando por WS.");
        setShowHelpModal(false);
        setHelpMessage("");
      } else {
        // En caso de que la respuesta del backend no tenga status = 1 pero sin arrojar error (opcional si el backend devuelve status = 1)
        showAlertError("Aviso", res.msg || "Correo no encontrado");
        setShowHelpModal(false);
        setHelpMessage("");
      }
    } catch (error) {
      console.error(error);
      showAlertError("Error", "No se pudo enviar el mensaje de ayuda.");
    } finally {
      setHelpLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 transition-opacity lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 shadow-xl transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:h-screen overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-[#8890A4]">
          <img src="/assets/logo-2ra.png" alt="2ra Logo" className="h-10 w-auto object-contain" />
          {/* Close button for mobile */}
          <button
            className="lg:hidden text-slate-500 hover:text-slate-700"
            onClick={() => setSidebarOpen(false)}
          >
            <FiLogOut className="rotate-180" size={20} />
          </button>
        </div>
        <nav className="p-4 space-y-1.5 overflow-y-auto h-[calc(100vh-80px)] custom-scrollbar">
          <Link
            to=""
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-semibold transition-all duration-200"
          >
            <FiHome size={18} /> Dashboard
          </Link>

          {/* Submenú: Inventario */}
          <div className="space-y-1">
            <button
              onClick={() => setInventarioOpen(!inventarioOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-100 text-slate-700 font-medium transition-colors"
            >
              <span className="flex items-center gap-3">
                <FiBox size={18} className="text-slate-500" /> Inventario
              </span>
              <span className="text-xs text-slate-400">{inventarioOpen ? "▲" : "▼"}</span>
            </button>
            {inventarioOpen && (
              <div className="ml-7 space-y-1 pl-2 border-l-2 border-slate-100">
                <Link
                  to="productos"
                  onClick={() => { setSidebarOpen(false); setUsuarioOpen(false); setIngresoOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiArchive size={16} /> Productos
                  </span>
                </Link>
                <Link
                  to="categorias"
                  onClick={() => { setSidebarOpen(false); setUsuarioOpen(false); setIngresoOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiCopy size={16} /> Categorías
                  </span>
                </Link>
              </div>
            )}
          </div>

          {/* Submenú: Ingresos */}
          <div className="space-y-1">
            <button
              onClick={() => setIngresoOpen(!ingresoOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-100 text-slate-700 font-medium transition-colors"
            >
              <span className="flex items-center gap-3">
                <FiFolderPlus size={18} className="text-slate-500" /> Ingresos
              </span>
              <span className="text-xs text-slate-400">{ingresoOpen ? "▲" : "▼"}</span>
            </button>
            {ingresoOpen && (
              <div className="ml-7 space-y-1 pl-2 border-l-2 border-slate-100">
                <Link
                  to="ingresos"
                  onClick={() => { setSidebarOpen(false); setInventarioOpen(false); setUsuarioOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiBookOpen size={16} /> Ingresos
                  </span>
                </Link>
                <Link
                  to="proveedores"
                  onClick={() => { setSidebarOpen(false); setInventarioOpen(false); setUsuarioOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiTruck size={16} /> Proveedores
                  </span>
                </Link>
              </div>
            )}
          </div>

          {/* Submenú: Usuarios */}
          <div className="space-y-1">
            <button
              onClick={() => setUsuarioOpen(!usuarioOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-100 text-slate-700 font-medium transition-colors"
            >
              <span className="flex items-center gap-3">
                <FiUsers size={18} className="text-slate-500" /> Usuarios
              </span>
              <span className="text-xs text-slate-400">{usuarioOpen ? "▲" : "▼"}</span>
            </button>
            {usuarioOpen && (
              <div className="ml-7 space-y-1 pl-2 border-l-2 border-slate-100">
                <Link
                  to="usuarios"
                  onClick={() => { setSidebarOpen(false); setInventarioOpen(false); setIngresoOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiUserPlus size={16} /> Usuarios
                  </span>
                </Link>
                <Link
                  to="accesos"
                  onClick={() => { setSidebarOpen(false); setInventarioOpen(false); setIngresoOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiUserX size={16} /> Accesos
                  </span>
                </Link>
                <Link
                  to="permisos"
                  onClick={() => { setSidebarOpen(false); setInventarioOpen(false); setIngresoOpen(false); }}
                  className="block px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-sm font-medium text-slate-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FiShieldOff size={16} /> Permisos
                  </span>
                </Link>
              </div>
            )}
          </div>

          {/* Submenú: Reportes */}
          <div className="space-y-1">
            <button
              onClick={() => {
                setUsuarioOpen(false);
                setInventarioOpen(false);
                setIngresoOpen(false);
                navigate("reportes/general");
              }}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-100 text-slate-700 font-medium transition-colors"
            >
              <span className="flex items-center gap-3">
                <FiBookOpen size={18} className="text-slate-500" /> Reportes
              </span>
            </button>
          </div>

          <div className="pt-2 mt-2 border-t border-slate-100 space-y-1">
            {/* Otras rutas */}
            <Link
              to="ventas"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiShoppingCart size={18} className="text-slate-500" /> Ventas
            </Link>

            <Link
              to="clientes"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiUserCheck size={18} className="text-slate-500" /> Clientes
            </Link>

            <Link
              to="sucursales"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiMapPin size={18} className="text-slate-500" /> Sucursales
            </Link>

            <Link
              to="pagosdetalle/list"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiDollarSign size={18} className="text-slate-500" /> Detalle de Pagos
            </Link>

            <Link
              to="configuracion"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiSettings size={18} className="text-slate-500" /> Configuración
            </Link>

            <Link
              to="modulos"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 text-slate-700 font-medium transition-colors"
            >
              <FiShare2 size={18} className="text-slate-500" /> Modulos
            </Link>
          </div>

          <div className="pt-4 mt-4 border-t border-slate-100 space-y-1">
            <button
              onClick={() => setShowHelpModal(true)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-indigo-50 text-indigo-600 font-medium w-full text-left transition-colors"
            >
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ayuda
            </button>
            <button
              onClick={cerrarSesion}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 text-red-600 font-medium w-full text-left transition-colors"
            >
              <FiLogOut size={18} /> Cerrar sesión
            </button>
          </div>
        </nav>
      </aside>

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top bar (mobile only) */}
        <header className="flex items-center justify-between bg-[#8890A4] px-4 py-3 shadow-sm border-b border-slate-200 lg:hidden">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <FiMenu size={24} />
          </button>
          <div className="flex items-center">
            <img src="/assets/logo-2ra.png" alt="2ra Logo" className="h-8 w-auto object-contain" />
          </div>
        </header>

        {/* Scrollable Main content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6 custom-scrollbar">
          <div className="max-w-7xl mx-auto space-y-6">
            {isRootDashboard ? (
              <div className="space-y-6 animate-fade-in">
                <div className="bg-white shadow-sm border border-slate-200 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-blue-500 to-indigo-600"></div>
                  <h2 className="text-2xl font-bold mb-3 text-slate-800">
                    Bienvenido al <span className="text-indigo-600">Panel de Control</span>
                  </h2>
                  <p className="text-slate-500 max-w-2xl">
                    Usa el menú lateral para navegar por las diferentes secciones del sistema.
                    Desde aquí puedes gestionar ventas, inventario, reportes y más de manera ágil.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                  {[
                    { title: "Compras", icon: <FiTruck size={24} />, desc: "Gestione sus órdenes de compra, proveedores y recepción de productos.", color: "text-emerald-600", bg: "bg-emerald-50" },
                    { title: "Inventario", icon: <FiBox size={24} />, desc: "Controle el stock en tiempo real, entradas y salidas con precisión.", color: "text-amber-600", bg: "bg-amber-50" },
                    { title: "Facturación", icon: <FiShoppingCart size={24} />, desc: "Emita facturas electrónicas, gestión de clientes y reportes detallados.", color: "text-blue-600", bg: "bg-blue-50" },
                    { title: "Reportes", icon: <FiBookOpen size={24} />, desc: "Visualice estadísticas y rendimiento del negocio con gráficos interactivos.", color: "text-purple-600", bg: "bg-purple-50" },
                    { title: "Usuarios", icon: <FiUsers size={24} />, desc: "Administre usuarios, roles y permisos de acceso al sistema.", color: "text-indigo-600", bg: "bg-indigo-50" },
                    { title: "Configuración", icon: <FiSettings size={24} />, desc: "Personalice parámetros fiscales, datos de la empresa y más.", color: "text-slate-600", bg: "bg-slate-100" },
                  ].map((modulo, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-lg hover:border-slate-200 transform transition-all duration-300 hover:-translate-y-1 group"
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${modulo.bg} ${modulo.color} group-hover:scale-110 transition-transform`}>
                        {modulo.icon}
                      </div>
                      <h4 className="text-lg font-bold mb-2 text-slate-800">
                        {modulo.title}
                      </h4>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {modulo.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="w-full animate-fade-in shadow-sm rounded-xl bg-white border border-slate-200">
                {/* Child routes rendered here */}
                <Outlet />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal de Ayuda */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-fade-in-up">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800">Solicitar Ayuda</h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSendHelp} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                ¿En qué necesitas ayuda? Por favor envíanos un mensaje y lo gestionaremos internamente.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Escribe aquí en qué necesitas ayuda..."
                  required
                  rows="4"
                  className="w-full py-2.5 px-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-gray-50 transition resize-none"
                ></textarea>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowHelpModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={helpLoading}
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex justify-center items-center"
                >
                  {helpLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : "Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



    </div>
  );
};

export default Dashboard;
